// 채팅 경량 마크다운 렌더러 — 바닐라 dcf.js 의 esc/mdInline/mdRowCells/
// isMdSeparator/mdToHtml 이식. 모든 원문은 esc() 로 먼저 이스케이프한 뒤
// 제한된 마크업(<b>·표·헤딩·hr·<br>)만 생성하므로 XSS 가 통과할 수 없다.

/** HTML 이스케이프 (바닐라 esc) */
export function esc(s: unknown): string {
  return String(s == null ? "" : s).replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

/** 인라인 마크다운 — **굵게** 만 지원 (바닐라 mdInline) */
export function mdInline(s: string): string {
  return esc(s).replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
}

/** 표 한 행을 셀 배열로 (바닐라 mdRowCells) */
function mdRowCells(line: string): string[] {
  let t = line.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|")) t = t.slice(0, -1);
  return t.split("|").map((c) => c.replace(/<br\s*\/?>/gi, " ").trim());
}

/** 마크다운 표 구분선 판정 (바닐라 isMdSeparator) */
function isMdSeparator(line: string): boolean {
  return /^[\s|:-]+$/.test(line) && line.includes("-") && line.includes("|");
}

/* ═══════════════════════════════════════════════════════════
   메인 채팅 페이지용 mdLite — 바닐라 app.js 의 escapeHtml/mdInline/
   tryParseChart/tableCell/mdLite 이식 (Phase 3-A).
   dcf 챗의 mdToHtml(아래)과 별개: 채팅 페이지는 링크·목록·코드블록·
   원문 링크 열 병합 등 확장 문법과 .art-table 계열 클래스를 쓴다.
   차트 펜스/JSON 스펙은 3-A 에서는 자리표시자 div(.chart-box[data-chart])만
   생성한다 — 스냅샷을 바닐라에서 열면 charts.js 가 hydrate 하고,
   React 렌더는 3-B 에서 붙는다.
   ═══════════════════════════════════════════════════════════ */

/** HTML 이스케이프 (바닐라 app.js escapeHtml — 작은따옴표 포함) */
export function escapeHtml(t: unknown): string {
  return String(t == null ? "" : t).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[
        c
      ] as string,
  );
}

/** 인라인 마크다운 — **굵게** + [text](url) 링크 + 맨 URL (바닐라 app.js mdInline) */
export function mdLiteInline(s: string): string {
  let h = escapeHtml(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // URL 은 이미 escapeHtml 을 거쳤고 서버가 인코딩해 보낸다 — 다시 인코딩하면
  // 프록시 링크의 %3A 가 %253A 로 깨지므로 그대로 둔다(공백만 보정).
  const href = (u: string) => u.replace(/ /g, "%20");
  // 절대(https)·상대(/agent/doc-proxy?…) 링크 모두 지원
  h = h.replace(
    /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^\s)]+)\)/g,
    (_m, t: string, u: string) =>
      `<a href="${href(u)}" target="_blank" rel="noopener">${t}</a>`,
  );
  // 남은 맨 URL
  h = h.replace(
    /(^|[\s>(])(https?:\/\/[^\s<)]+)/g,
    (_m, p: string, u: string) =>
      `${p}<a href="${href(u)}" target="_blank" rel="noopener">${u}</a>`,
  );
  return h;
}

/** 차트 스펙 판별 — 펜스 종류(```chart/```json/```)나 펜스 없이 온 JSON 모두 수용 */
export function isChartSpec(o: unknown): boolean {
  if (!o || typeof o !== "object" || Array.isArray(o)) return false;
  const c = o as Record<string, unknown>;
  if (typeof c.type !== "string") return false;
  // 위젯(financials·price)은 symbol/name 만으로도 유효 — 데이터는 위젯이 받아온다
  if (c.type === "financials" || c.type === "price") {
    return !!(c.symbol || c.name || Array.isArray(c.points));
  }
  return Array.isArray(c.series) || Array.isArray(c.data) || Array.isArray(c.points);
}

function tryParseChart(text: string): unknown | null {
  const t = String(text || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t) as unknown;
    return isChartSpec(o) ? o : null;
  } catch {
    return null;
  }
}

/** 차트 자리표시자 — data-chart 는 바닐라 charts.js hydrate 계약과 동일 */
export function chartHtml(spec: unknown): string {
  return `<div class="chart-box" data-chart="${encodeURIComponent(JSON.stringify(spec))}"></div>`;
}

// ── 표에서 차트 자동 생성 (바닐라 app.js chartFromMarkdownTable) ─────────
// 답변 본문의 마크다운 표를 읽어, 사람이 추세·규모를 바로 보도록 차트를 함께 그린다.
// 두 가지 배치를 모두 지원한다.
//   세로형: 첫 열이 연도/날짜   → x축 = 연도, 시리즈 = 나머지 수치 열
//   전치형: 헤더가 연도(2024년…) → x축 = 항목, 시리즈 = 연도  (예: 주요 재무지표 표)
const TBL_YEAR = /^\s*(19|20)\d{2}\s*(년|년도|Y|년말)?\s*$/;
const TBL_DATE = /^\s*(19|20)\d{2}[-./]\d{1,2}([-./]\d{1,2})?\s*$/;
const TBL_SKIP_COL = /^(변화|증감|비고|전년\s*대비|YoY|구분)/i; // 비율·설명 열은 축이 달라 제외
const TBL_PL_KEYS = ["매출", "영업이익", "영업손실", "당기순이익", "순이익", "EBITDA"];

function tblNum(v: unknown): number | null {
  const t = String(v == null ? "" : v)
    .replace(/<[^>]*>/g, "")
    .replace(/[,\s]/g, "")
    .replace(/[△▲]/g, "-")
    .replace(/[▼]/g, "-")
    .replace(/[()]/g, "") // (1,234) 형태의 음수 표기는 괄호만 제거
    .replace(/(억원|조원|백만원|천원|원|억|조|%|배|주|건|명)$/u, "");
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  return parseFloat(t);
}
function tblUnit(cells: string[]): string {
  for (const c of cells) {
    const m = String(c).match(/(조원|억원|백만원|천원|원|%|배)/u);
    if (m) return m[1];
  }
  return "";
}
function tblIsRatio(cells: string[]): boolean {
  const hit = cells.filter((c) => /%/.test(String(c))).length;
  return hit >= Math.max(1, Math.floor(cells.length * 0.6));
}

interface TblSeries {
  name: string;
  type: string;
  data: Array<number | null>;
}

export function chartFromMarkdownTable(
  head: string[],
  body: string[][],
): { type: string; x: string[]; series: TblSeries[]; yLabel: string } | null {
  if (!head || head.length < 2 || !body || body.length < 2) return null;
  const plain = (v: unknown) => String(v == null ? "" : v).replace(/<[^>]*>/g, "").trim();
  const H = head.map(plain);

  // ① 전치형 — 헤더 2번째 열부터가 연도
  const yearCols: number[] = [];
  for (let c = 1; c < H.length; c++) {
    if (TBL_YEAR.test(H[c]) && !TBL_SKIP_COL.test(H[c])) yearCols.push(c);
  }
  if (yearCols.length >= 2) {
    let rows = body
      .map((r) => ({
        label: plain(r[0]),
        vals: yearCols.map((c) => tblNum(r[c])),
        cells: yearCols.map((c) => plain(r[c])),
      }))
      .filter((r) => r.label && r.vals.every((v) => v !== null) && !tblIsRatio(r.cells));
    if (rows.length < 2) return null;
    // 손익 항목이 있으면 그것만 — 규모가 제각각인 항목을 한 축에 섞지 않는다
    const pl = rows.filter((r) => TBL_PL_KEYS.some((k) => r.label.includes(k)));
    rows =
      pl.length >= 2
        ? pl
        : rows
            .slice()
            .sort(
              (a, b) =>
                Math.max(...b.vals.map((v) => Math.abs(v as number))) -
                Math.max(...a.vals.map((v) => Math.abs(v as number))),
            )
            .slice(0, 5);
    const unit = tblUnit(rows.flatMap((r) => r.cells));
    // 연도를 x축 그룹으로, 항목(매출·이익)을 막대로 — 연도별 구성이 한눈에 들어온다
    return {
      type: "bar",
      x: yearCols.map((c) => H[c].replace(/\s*년\s*$/u, "")),
      series: rows.map((r) => ({ name: r.label, type: "bar", data: r.vals })),
      yLabel: unit,
    };
  }

  // ② 세로형 — 첫 열이 연도/날짜
  const xs = body.map((r) => plain(r[0]));
  if (!xs.every((v) => TBL_YEAR.test(v) || TBL_DATE.test(v))) return null;
  const series: Array<TblSeries & { _scale: number }> = [];
  for (let c = 1; c < H.length; c++) {
    if (TBL_SKIP_COL.test(H[c])) continue;
    const cells = body.map((r) => plain(r[c]));
    if (tblIsRatio(cells)) continue;
    const vals = body.map((r) => tblNum(r[c]));
    if (vals.some((v) => v === null)) continue;
    series.push({
      name: H[c],
      type: "bar",
      data: vals,
      _scale: Math.max(...vals.map((v) => Math.abs(v as number))),
    });
  }
  if (!series.length) return null;
  const unit = tblUnit(body.flatMap((r) => r.slice(1).map(plain)));
  const out = series.slice(0, 4).map((sr) => ({ name: sr.name, type: "bar", data: sr.data }));
  return { type: "bar", x: xs.map((v) => v.replace(/\s*년\s*$/u, "")), series: out, yLabel: unit };
}

// 표 셀 — 셀 전체가 링크 하나뿐이면 '원문 보기' 아이콘으로 바꾼다.
const LINK_ONLY = /^\s*<a ([^>]*)>([^<]*)<\/a>\s*$/;
const EXT_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17L17 7M9 7h8v8"/></svg>';
function tableCell(c: string): string {
  const html = mdLiteInline(c);
  const m = html.match(LINK_ONLY);
  if (!m) return html;
  const label = (m[2] || "").trim();
  // "원문"·"보기" 같은 관용 문구면 툴팁을 중복시키지 않는다
  const tip =
    !label || /^(원문|링크|보기|원문\s*보기)$/.test(label)
      ? "원문 보기"
      : `${label} 원문 보기`;
  return `<a ${m[1]} class="doc-link" title="${escapeHtml(tip)}" aria-label="${escapeHtml(tip)}">${EXT_ICON}</a>`;
}

/** 채팅 페이지 경량 마크다운 (바닐라 app.js mdLite) —
 *  코드펜스(차트 스펙이면 자리표시자)·GFM 표(원문 링크 열 병합)·헤딩·목록·구분선·문단 */
export function mdLite(text: string, opts?: { noCharts?: boolean }): string {
  const noCharts = !!(opts && opts.noCharts);
  const lines = String(text || "").split("\n");
  const out: string[] = [];
  let i = 0;
  const BULLET = /^\s*[-·•]\s+/;
  const NUM = /^\s*\d+[.)]\s+/;
  while (i < lines.length) {
    const line = lines[i];
    // 코드 펜스 — 내용이 차트 스펙이면 차트 자리표시자로, 아니면 코드 블록으로
    const fence = line.match(/^\s*```+\s*([A-Za-z0-9_-]*)\s*$/);
    if (fence) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```+\s*$/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      const closed = i < lines.length;
      i++; // 닫는 펜스
      const body = buf.join("\n");
      const spec = noCharts ? null : tryParseChart(body);
      if (spec) out.push(chartHtml(spec));
      else if (closed && body.trim())
        out.push(`<pre class="code-block">${escapeHtml(body)}</pre>`);
      // 스트리밍 중 미완성 블록은 조용히 건너뛴다
      continue;
    }
    // GFM table: header row + separator row
    if (
      /^\s*\|.*\|\s*$/.test(line) &&
      i + 1 < lines.length &&
      /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1])
    ) {
      const cells = (r: string) =>
        r
          .trim()
          .replace(/^\||\|$/g, "")
          .split("|")
          .map((c) => c.replace(/<br\s*\/?>/gi, " ").trim());
      const head = cells(line);
      i += 2;
      const body: string[][] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        body.push(cells(lines[i]));
        i++;
      }
      // 원문 링크 열은 따로 두면 제목과 동떨어져 보인다 — 링크를 제목 열 옆에
      // 아이콘으로 붙이고 그 열은 표에서 뺀다.
      let thCells = head;
      let trRows: string[][] = body;
      const linkCol = head.findIndex(
        (h, ci) =>
          /^(원문|링크|바로가기)$/.test(h.trim()) ||
          (h.trim() === "" &&
            body.length > 0 &&
            body.every((r) => !r[ci] || LINK_ONLY.test(mdLiteInline(r[ci] || "")))),
      );
      if (linkCol > 0 && body.some((r) => LINK_ONLY.test(mdLiteInline(r[linkCol] || "")))) {
        thCells = head.filter((_, ci) => ci !== linkCol);
        // 아이콘은 제목 성격의 열에 붙인다 — 제목류 헤더가 없으면 첫 열로 폴백
        const titleCol = Math.max(
          0,
          thCells.findIndex((h) => /제목|기업명|회사명|종목|이름/.test(String(h))),
        );
        trRows = body.map((r) => {
          const icon = LINK_ONLY.test(mdLiteInline(r[linkCol] || ""))
            ? tableCell(r[linkCol])
            : "";
          return r
            .filter((_, ci) => ci !== linkCol)
            .map(
              (c, ci) =>
                (ci === titleCol && icon
                  ? `<span class="cell-doc">${mdLiteInline(c)} ${icon}</span>`
                  : null) ?? c,
            );
        });
      }
      const th = thCells.map((c) => `<th>${mdLiteInline(c)}</th>`).join("");
      const tr = trRows
        .map(
          (r) =>
            `<tr>${r
              .map(
                (c) =>
                  `<td>${String(c).startsWith('<span class="cell-doc">') ? c : tableCell(c)}</td>`,
              )
              .join("")}</tr>`,
        )
        .join("");
      out.push(
        `<div class="art-body"><table class="art-table"><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></div>`,
      );
      // 표 자동 차트 — 연도/날짜 축이 읽히면 추세를 바로 볼 차트를 함께 그린다
      if (!noCharts) {
        const tspec = chartFromMarkdownTable(head, body);
        if (tspec) out.push(chartHtml(tspec));
      }
      continue;
    }
    // 헤딩 — 시각적 위계를 위해 실제 h태그로 렌더 (h1~2급 → h3, h3~4급 → h4)
    const h = line.match(/^\s*(#{1,4})\s+(.*)$/);
    if (h) {
      const tag = h[1].length <= 2 ? "h3" : "h4";
      out.push(`<${tag}>${mdLiteInline(h[2])}</${tag}>`);
      i++;
      continue;
    }
    // 불릿 목록 — '-'·'·'·'•' 시작 줄을 <ul>로 통일
    if (BULLET.test(line)) {
      const items: string[] = [];
      while (i < lines.length && BULLET.test(lines[i])) {
        items.push(lines[i].replace(BULLET, ""));
        i++;
      }
      out.push(`<ul>${items.map((x) => `<li>${mdLiteInline(x)}</li>`).join("")}</ul>`);
      continue;
    }
    // 번호 목록 → <ol>
    if (NUM.test(line)) {
      const items: string[] = [];
      while (i < lines.length && NUM.test(lines[i])) {
        items.push(lines[i].replace(NUM, ""));
        i++;
      }
      out.push(`<ol>${items.map((x) => `<li>${mdLiteInline(x)}</li>`).join("")}</ol>`);
      continue;
    }
    // 구분선(---)은 여백으로만 처리
    if (/^\s*-{3,}\s*$/.test(line)) {
      i++;
      continue;
    }
    // collect a paragraph block
    const buf: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^\s*\|.*\|\s*$/.test(lines[i]) &&
      !/^\s*#{1,4}\s/.test(lines[i]) &&
      !BULLET.test(lines[i]) &&
      !NUM.test(lines[i]) &&
      !/^\s*-{3,}\s*$/.test(lines[i])
    ) {
      buf.push(lines[i]);
      i++;
    }
    if (buf.length) {
      const spec = noCharts ? null : tryParseChart(buf.join("\n"));
      if (spec) out.push(chartHtml(spec));
      else out.push(`<p>${buf.map(mdLiteInline).join("<br>")}</p>`);
    } else if (i < lines.length && lines[i].trim() !== "") {
      // 어느 분기도 소비하지 못한 줄 — 예: 구분행이 아직 도착하지 않은 표 시작 줄
      // (스트리밍 경계). 여기서 소비하지 않으면 i 가 전진하지 못해 무한 루프.
      out.push(`<p>${mdLiteInline(lines[i])}</p>`);
      i++;
    }
    while (i < lines.length && lines[i].trim() === "") i++;
  }
  return out.join("");
}

/** 채팅용 마크다운 → HTML (바닐라 mdToHtml) — 표·헤딩·hr·굵게·줄바꿈 지원 */
export function mdToHtml(text: string): string {
  const lines = String(text).replace(/\r/g, "").split("\n");
  let html = "";
  let i = 0;
  while (i < lines.length) {
    if (lines[i].includes("|") && i + 1 < lines.length && isMdSeparator(lines[i + 1])) {
      const header = mdRowCells(lines[i]);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
        rows.push(mdRowCells(lines[i]));
        i++;
      }
      let t =
        '<div class="chat-table-wrap"><table class="chat-table"><thead><tr>' +
        header.map((h) => `<th>${mdInline(h)}</th>`).join("") +
        "</tr></thead><tbody>";
      t += rows
        .map((r) => "<tr>" + r.map((c) => `<td>${mdInline(c)}</td>`).join("") + "</tr>")
        .join("");
      t += "</tbody></table></div>";
      html += t;
      continue;
    }
    const hm = lines[i].match(/^\s*#{1,6}\s+(.*\S)\s*$/);
    if (hm) {
      html += `<div class="chat-h">${mdInline(hm[1])}</div>`;
      i++;
      continue;
    }
    if (/^\s*([-*_])\1{2,}\s*$/.test(lines[i])) {
      html += '<hr class="chat-hr">';
      i++;
      continue;
    }
    html += mdInline(lines[i]) + (i < lines.length - 1 ? "<br>" : "");
    i++;
  }
  return html;
}
